import 'regenerator-runtime/runtime';
import { call, delay, put, race, take } from 'redux-saga/effects';
import { PollingResponseModel, PollingStatus } from '@ps20/bff-models';
import { App } from '@ps20-frontend/frontend-models';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
} from '@ps20-frontend/shop-integration-redux';
import { CallEffect } from '@redux-saga/core/effects';

export enum POLLING_HELPER_EVENTS {
  FETCH_SUCCESSFUL = 'POLLING_HELPER_FETCH_SUCCESSFUL',
  FETCH_FAILED = 'POLLING_HELPER_FETCH_FAILED',
  CANCEL_POLLING = 'POLLING_HELPER_CANCEL_POLLING',
  FINISHED = 'POLLING_HELPER_POLLING_FINISHED',
}

export const pollingTimeIntervals = (): {
  POLLING_INTERVAL_MS: number;
  MAX_WAITING_TIME_MS: number;
} => ({
  POLLING_INTERVAL_MS: 4000,
  MAX_WAITING_TIME_MS: 60000 * 3,
});

export interface PollingOptions {
  pollingUrl: string;
  successEvent: string;
  errorEvent: string;
}

export interface PollingErrorResponseModel {
  data: PollingErrorMessageResponseModel;
}

export interface PollingErrorMessageResponseModel {
  message: string;
  status: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* startPolling(pollingOptions: PollingOptions): any {
  const { timedOut, failed } = yield race({
    pollTask: call(pollingTask, pollingOptions),
    canceled: take(POLLING_HELPER_EVENTS.CANCEL_POLLING),
    timedOut: delay(pollingTimeIntervals().MAX_WAITING_TIME_MS),
    failed: take(POLLING_HELPER_EVENTS.FETCH_FAILED),
  });

  if (failed) {
    yield put({ type: pollingOptions.errorEvent, payload: failed.payload });
  } else if (timedOut) {
    yield put({ type: pollingOptions.errorEvent });
  }
  yield put({ type: POLLING_HELPER_EVENTS.FINISHED });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* pollingTask(pollingOptions: PollingOptions): any {
  while (true) {
    yield call(doPollingRequest, pollingOptions.pollingUrl);
    const result: DoRequestResponse<PollingResponseModel<unknown>> = yield take(
      POLLING_HELPER_EVENTS.FETCH_SUCCESSFUL,
    );
    if (pollingSuccessfulFinished(result.payload)) {
      yield put({ type: pollingOptions.successEvent, payload: result.payload });
      return yield put({ type: POLLING_HELPER_EVENTS.CANCEL_POLLING });
    } else if (pollingFinishedWithError(result.payload)) {
      yield put({
        type: POLLING_HELPER_EVENTS.FETCH_FAILED,
        payload: result.payload,
      });
    }

    yield delay(pollingTimeIntervals().POLLING_INTERVAL_MS);
  }
}

export const doPollingRequest = function* (pollingUrl: string): Generator<CallEffect> {
  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    new GlobalActions().doRequest({
      appId: App.SHOP_CHECKOUT,
      url: pollingUrl,
      method: HTTP_METHOD.GET,
      successEvent: POLLING_HELPER_EVENTS.FETCH_SUCCESSFUL,
      errorEvent: POLLING_HELPER_EVENTS.FETCH_FAILED,
    }),
  );
};

function pollingSuccessfulFinished(pollingResult: PollingResponseModel<unknown>): boolean {
  return pollingResult.pollingStatus === PollingStatus.FINISHED;
}

function pollingFinishedWithError(payload: PollingResponseModel<unknown>): boolean {
  return payload.pollingStatus === PollingStatus.FINISHED_WITH_ERROR;
}

export function getValidationMessage(errorAction: { payload?: { response?: { data?: { message: string } } } }): string {
  const splittedErrors: string[] = errorAction.payload?.response?.data?.message.split('\n');
  return splitValidationMessage(splittedErrors);
}

export function splitValidationMessage(splittedErrors: string[]): string {
  const message: string[] = [];
  splittedErrors.forEach(splittedError => {
    message.push(`${splittedError}<br>`);
  });
  return message.join('');
}
